import React, { use, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import notFoundNewConsumerApp from 'commons/notFoundNewConsumerApp';
import PDPDesktop from './PDPDesktop';
import PDPMobile from './PDPMobile';
import Component404 from '../404';
import MobileAndTabletOnly from '../MobileAndTabletOnly';
import DesktopOnly from '../DesktopOnly';
import PDPSEOTags from './PDPSEOTags';
import PDPFooter from './PDPFooter';
import PDPContext from './state/context/PDPContext';
import usePDPLuxAnalytics from './hooks/usePDPLuxAnalytics';
import usePDPCreateContextValue from './hooks/usePDPCreateContextValue';
import PDPPageAnalytics from './PDPPageAnalytics';
import SuspenseWithLoader from '../SuspenseWithLoader';
import PDPPlaceholder from './PDPPlaceholder';
import buildPdpDesktopHeroLcpUrl from './PDPDesktopHeroGallery/helpers/buildPdpDesktopHeroLcpUrl';

const PDPInner = ({
  slugOverride = null,
  isEditOrder = false,
  pdpIsModularBYOProduct = false,
  productOptionsPromise,
  productHeroImagePromise,
}) => {
  const params = useParams();
  const slug = slugOverride ?? params.slug ?? null;
  const productOptions = productOptionsPromise
    ? use(productOptionsPromise)
    : null;

  const productHeroImageFromServer = productHeroImagePromise
    ? use(productHeroImagePromise)
    : null;

  const pdpContextValue = usePDPCreateContextValue({
    isEditOrder,
    pdpIsModularBYOProduct,
    slug,
    productOptions,
  });

  usePDPLuxAnalytics();

  const productHeroImage = useMemo(() => {
    if (productHeroImageFromServer?.src) {
      return productHeroImageFromServer;
    }

    const heroUrl =
      pdpContextValue.basicDetails?.data?.hero_images?.[0]?.img?.url;

    if (!heroUrl) {
      return null;
    }

    return {
      src: heroUrl,
      width: 1600,
      height: 2400,
      alt: pdpContextValue.basicDetails?.data?.name
        ? String(pdpContextValue.basicDetails.data.name)
        : '',
    };
  }, [pdpContextValue.basicDetails, productHeroImageFromServer]);

  if (pdpContextValue.basicDetails.isProductNotFound) {
    if (!isNewConsumerApp) {
      return <Component404 />;
    }

    return notFoundNewConsumerApp();
  }

  const {
    discontinuedCategoryMsg = null,
    isDiscontinued = false,
    isEFGInternal,
    isOutOfStock = false,
    showNotification,
  } = pdpContextValue.stockAvailability?.data || {};

  const heroPreloadHref = productHeroImage?.src
    ? buildPdpDesktopHeroLcpUrl(productHeroImage.src) ?? productHeroImage.src
    : null;

  return (
    <PDPContext.Provider value={pdpContextValue}>
      {heroPreloadHref ? (
        <Helmet>
          <link
            rel="preload"
            as="image"
            href={heroPreloadHref}
            media="(min-width: 1024px)"
            fetchPriority="high"
          />
        </Helmet>
      ) : null}
      <PDPSEOTags isOutOfStock={isOutOfStock} />

      <MobileAndTabletOnly>
        <PDPMobile
          slug={slug}
          isEditOrder={isEditOrder}
          isOutOfStock={isOutOfStock}
          showOutOfStockNotification={showNotification}
          pdpIsModularBYOProduct={pdpIsModularBYOProduct}
          showDiscontinuedNotification={isDiscontinued}
          discontinuedCategoryMsg={
            isDiscontinued ? discontinuedCategoryMsg : null
          }
        />
      </MobileAndTabletOnly>

      <DesktopOnly>
        <PDPDesktop
          slug={slug}
          isEditOrder={isEditOrder}
          isOutOfStock={isOutOfStock}
          showOutOfStockNotification={showNotification}
          pdpIsModularBYOProduct={pdpIsModularBYOProduct}
          isEFGInternal={isEFGInternal}
          showDiscontinuedNotification={isDiscontinued}
          discontinuedCategoryMsg={
            isDiscontinued ? discontinuedCategoryMsg : null
          }
          productHeroImage={productHeroImage}
        />
      </DesktopOnly>

      <PDPPageAnalytics />
      <PDPFooter />
    </PDPContext.Provider>
  );
};

const PDP = props => (
  <SuspenseWithLoader fallback={<PDPPlaceholder />}>
    <PDPInner {...props} />
  </SuspenseWithLoader>
);

PDP.propTypes = {
  slugOverride: PropTypes.string,
  isEditOrder: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  productHeroImagePromise: PropTypes.object,
};

export default PDP;
